import React, { useState, useEffect } from 'react';
import {
  Info, ArrowClockwise, Trash, UserMinus, Tray, X, Check, CaretLeft, CaretRight,
} from '@phosphor-icons/react';
import {
  getBranchNames,
  getGroupCode,
  getTickets,
  getGuarantormvoDetails,
  getMvoReferenceIds,
  saveData,
  deleteGuarantorDetails,
} from '../api/connectionRegistryApi';
import { useLoginName } from '../utils/auditUser';
import SearchableSelect from './SearchableSelect';
import Toast from './Toast';
import { useToast } from '../utils/useToast';
import SectionLock from './SectionLock';
import { NO_DATA_MESSAGE, NO_DATA_SUMMARY, isEmptyResult } from '../utils/noData';

const ROWS_PER_PAGE = 10;

// ── Row identity / field helpers (mirrors the Angular component's getters) ──

// Primary row identity used for selection tracking, and the key used to
// resolve v_reference_id via GetMVOReferenceIds.
function getGuarantorId(row) {
  if (!row) return null;
  return row.guarantorId ?? row.guarantor_id ?? row.GuarantorId ?? null;
}

// Separate identifier — this is what goes into contactIds on delete.
// Do NOT confuse this with guarantorId.
function getContactId(row) {
  if (!row) return null;
  return row.contact_id ?? row.contactId ?? row.ContactId ?? null;
}

// The MVO reference string (e.g. "MVO2530227") shown in the grid. Resolved via
// GetMVOReferenceIds when the details call returns it null.
function getReferenceValue(row) {
  if (!row) return null;
  const value = row?.v_reference_id ?? row?.vReferenceId ?? row?.referenceId ?? row?.reference_id;
  if (Array.isArray(value)) {
    const joined = value.filter(Boolean).join(', ');
    return joined || null;
  }
  return value ?? null;
}

function normalizeGuarantorRecords(response) {
  if (Array.isArray(response)) return response;
  if (!response) return [];
  if (Array.isArray(response.data)) return response.data;
  if (Array.isArray(response.records)) return response.records;
  if (Array.isArray(response.items)) return response.items;
  return [response];
}

function MvoGuarantorDeleteForm() {
  const loginName = useLoginName();
  const [formKey, setFormKey] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  const { toast, setToast, showToast } = useToast();

  const [branchlist, setBranchlist] = useState([]);
  const [branchCodeList, setBranchCodeList] = useState([]); // groups
  const [tickets, setTickets] = useState([]);
  const [guarantorRecords, setGuarantorRecords] = useState([]);
  const [selectedGuarantors, setSelectedGuarantors] = useState([]);
  const [page, setPage] = useState(0);

  const [cao, setCao] = useState('');
  const [group, setGroup] = useState('');
  const [ticket, setTicket] = useState('');
  const [reason, setReason] = useState('');

  // Mirrors Angular's per-control `touched` state: a field only shows its error
  // once the user has interacted with it, or once Delete has been pressed.
  const [touched, setTouched] = useState({});
  const markTouched = (field) => setTouched((prev) => ({ ...prev, [field]: true }));

  const [branchschema, setBranchschema] = useState(null);
  const [branchCode, setBranchCode] = useState(null);
  const [groupCode, setGroupCode] = useState(null);
  const [chitgroupId, setChitgroupId] = useState(null);

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const [pendingReason, setPendingReason] = useState('');
  const [pendingDeletePayload, setPendingDeletePayload] = useState(null);

  useEffect(() => {
    getBranchNames().then((res) => setBranchlist(res || [])).catch(() => setBranchlist([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formKey]);

  const preventExceedLength = (e, max) => {
    const value = e.target.value ?? '';
    const navKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'];
    if (value.length >= max && !navKeys.includes(e.key)) {
      e.preventDefault();
    }
  };

  const resetFromTicket = () => {
    setGuarantorRecords([]);
    setSelectedGuarantors([]);
    setReason('');
    setPage(0);
  };

  const resetFromGroup = () => {
    setGroupCode(null);
    setChitgroupId(null);
    setTicket('');
    setTickets([]);
    resetFromTicket();
  };

  const resetFromBranch = () => {
    setBranchschema(null);
    setBranchCode(null);
    setGroup('');
    setBranchCodeList([]);
    resetFromGroup();
  };

  const handleBranchChange = async (branchName) => {
    setCao(branchName);
    markTouched('cao');
    resetFromBranch();
    if (!branchName) return;

    const selected = branchlist.find((x) => x.branch_name === branchName);
    if (!selected) return;

    const schema = selected.branch_code;
    setBranchschema(schema);
    setBranchCode(schema);

    try {
      const res = await getGroupCode(schema);
      setBranchCodeList(res || []);
      if (isEmptyResult(res)) showToast('warn', NO_DATA_SUMMARY, NO_DATA_MESSAGE);
    } catch {
      setBranchCodeList([]);
    }
  };

  const handleGroupChange = async (groupCodeValue) => {
    setGroup(groupCodeValue);
    markTouched('group');
    resetFromGroup();
    if (!groupCodeValue) return;

    const selected = branchCodeList.find((x) => x.group_Code === groupCodeValue);
    if (!selected) return;

    setGroupCode(selected.group_Code);
    setChitgroupId(selected.chitgroup_id);

    try {
      const res = await getTickets(branchschema, selected.group_Code);
      setTickets(res || []);
      if (isEmptyResult(res)) showToast('warn', NO_DATA_SUMMARY, NO_DATA_MESSAGE);
    } catch {
      setTickets([]);
    }
  };

  /**
   * getGuarantormvoDetails returns rows like:
   * { guarantorId: 37517, contact_id: 22332, guarantorName: '...', v_reference_id: null }
   *
   * v_reference_id is frequently null here. When it is, we resolve it separately
   * via getMvoReferenceIds(guarantorId, branchschema), which returns the actual
   * MVO reference string (e.g. "MVO2530227") for that guarantorId.
   *
   * guarantorId and contact_id are DIFFERENT identifiers and must never be
   * substituted for one another:
   *   - contact_id   -> sent as contactIds to deleteGuarantorDetails
   *   - guarantorId  -> sent as guarantorMVOIds to deleteGuarantorDetails
   *                     (it is the tbl_mst_guarantor_mvo_id the backend matches on)
   */
  const handleTicketChange = async (ticketno) => {
    setTicket(ticketno);
    markTouched('ticket');
    resetFromTicket();
    if (!ticketno) return;

    try {
      const res = await getGuarantormvoDetails(branchschema, branchCode, groupCode, ticketno);
      const rows = normalizeGuarantorRecords(res);
      if (!rows.length) {
        setGuarantorRecords([]);
        setSelectedGuarantors([]);
        showToast('warn', NO_DATA_SUMMARY, NO_DATA_MESSAGE);
        return;
      }

      const resolvedRefs = await Promise.all(rows.map(async (row) => {
        const existingRef = getReferenceValue(row);
        if (existingRef) return existingRef;

        const gId = getGuarantorId(row);
        if (!gId) return null;

        try {
          const refRes = await getMvoReferenceIds(gId, branchschema);
          return getReferenceValue(refRes?.[0] ?? null);
        } catch {
          return null;
        }
      }));

      rows.forEach((row, i) => {
        if (!getReferenceValue(row) && resolvedRefs[i]) {
          row.v_reference_id = resolvedRefs[i];
        }
      });

      setGuarantorRecords(rows);
      setSelectedGuarantors([]);
      setPage(0);
    } catch (err) {
      console.error('Failed to fetch guarantor details:', err);
      showToast('error', 'Load Failed', 'Failed to fetch guarantor details.');
    }
  };

  const isSelected = (row) => {
    const rowId = getGuarantorId(row);
    return selectedGuarantors.some((g) => getGuarantorId(g) === rowId);
  };

  const toggleSelect = (row) => {
    const rowId = getGuarantorId(row);
    if (isSelected(row)) {
      setSelectedGuarantors((prev) => prev.filter((g) => getGuarantorId(g) !== rowId));
    } else {
      setSelectedGuarantors((prev) => [...prev, row]);
    }
  };

  const clearSelection = () => setSelectedGuarantors([]);

  const caoInvalid = touched.cao && !cao;
  const groupInvalid = touched.group && !group;
  const ticketInvalid = touched.ticket && !ticket;
  const reasonInvalid = touched.reason && (reason.trim().length < 10 || reason.length > 200);

  const handleDeleteSuccess = (deletedIds) => {
    setGuarantorRecords((prev) => prev.filter((g) => !deletedIds.includes(getGuarantorId(g))));
    setSelectedGuarantors([]);
    setIsSaving(false);
    setShowConfirmModal(false);
    showToast('success', 'Success', 'Audit saved and guarantor(s) deleted successfully.', true);
    handleClear();
  };

  /** Step 1: validate + stage the pending delete, open the confirm modal (replaces Angular's window.confirm). */
  const confirmDelete = () => {
    setTouched({ cao: true, group: true, ticket: true, reason: true });

    if (reason.trim().length < 10 || reason.length > 200) {
      showToast('warn', 'Incomplete Form', 'Please enter a valid reason (min 10 characters).');
      return;
    }

    if (selectedGuarantors.length === 0) {
      showToast('warn', 'No Selection', 'Please select at least one guarantor record to delete.');
      return;
    }

    // Guard: every selected row must have both a contact_id and a resolved
    // v_reference_id before we call deleteGuarantorDetails — a row with no
    // reference has no tbl_trans_mvo_allotment record behind it, so deleting it
    // would drop the guarantor while leaving the allotment side untouched.
    const missingData = selectedGuarantors.filter(
      (g) => !getContactId(g) || !getReferenceValue(g),
    );
    if (missingData.length > 0) {
      showToast('warn', 'Incomplete Form', 'One or more selected guarantors are missing a contact id or MVO reference id. Please refresh and try again.');
      return;
    }

    setPendingCount(selectedGuarantors.length);
    setPendingReason(reason);
    setPendingDeletePayload({ guarantors: [...selectedGuarantors] });
    setShowConfirmModal(true);
  };

  const cancelConfirm = () => {
    if (isSaving) return;
    setShowConfirmModal(false);
    setPendingDeletePayload(null);
  };

  /** Step 2: fires only after the user clicks "Yes, Delete" in the modal. */
  const doDelete = async () => {
    if (!pendingDeletePayload) return;

    setIsSaving(true);

    const guarantorsToDelete = pendingDeletePayload.guarantors;

    // contactIds and guarantorMVOIds are built index-aligned from the SAME
    // array/order, so position i in one always corresponds to position i in
    // the other for a given guarantor row — they can never mismatch.
    const contactIds = guarantorsToDelete
      .map((g) => getContactId(g))
      .filter((id) => id !== null && id !== undefined && id !== '')
      .join(',');

    // The API's `guarantorMVOIds` is matched against tbl_mst_guarantor_mvo_id
    // (DeleteGuarantorDetails -> DELETE FROM <schema>.tbl_trans_mvo_allotment
    // WHERE tbl_mst_guarantor_mvo_id IN (...)), and GetGuarantormvoDetails
    // returns that same column as `guarantorId`. So this list is built from
    // guarantorId — NOT from v_reference_id, which is the display reference
    // ("MVO2530227") and would fail the bigint comparison.
    const guarantorMVOIds = guarantorsToDelete
      .map((g) => getGuarantorId(g))
      .filter((id) => id !== null && id !== undefined && id !== '')
      .join(',');

    const deletedIds = guarantorsToDelete.map((g) => getGuarantorId(g));
    const ticketNo = Number(ticket);

    // Full snapshot of what is being deleted, kept in the audit record so the
    // deleted data isn't lost once it's removed from guarantorRecords.
    const deletedGuarantorsSnapshot = guarantorsToDelete.map((g) => ({
      guarantorId: getGuarantorId(g),
      contactId: getContactId(g),
      guarantorName: g.guarantorName ?? g.guarantor_name ?? g.name ?? null,
      v_reference_id: getReferenceValue(g),
      raw: g,
    }));

    const audit = {
      schemaName: branchschema,
      loginName,
      loginTime: new Date().toISOString(),
      changeType: 'MVO_GUARANTOR_DELETE',
      oldData: {
        branchSchema: branchschema,
        branchCode,
        groupCode,
        chitGroupId: chitgroupId,
        ticketNo,
        contactIds,
        guarantorMVOIds,
        guarantors: deletedGuarantorsSnapshot,
      },
      newData: {
        status: 'DELETED',
        deletedGuarantors: deletedGuarantorsSnapshot,
      },
      reason,
    };

    try {
      await saveData([audit]);

      try {
        await deleteGuarantorDetails(branchschema, chitgroupId, ticketNo, contactIds, guarantorMVOIds);
        handleDeleteSuccess(deletedIds);
      } catch (err) {
        const isSuccess = err?.response?.data?.text?.toLowerCase?.() === 'success'
          || err?.response?.data?.toLowerCase?.() === 'success';

        if (isSuccess) {
          handleDeleteSuccess(deletedIds);
        } else {
          setIsSaving(false);
          setShowConfirmModal(false);
          console.error('Delete failed:', err);
          showToast('warn', 'Partial Success', 'Audit saved, but delete failed. Please try again.', true);
        }
      }
    } catch (err) {
      setIsSaving(false);
      setShowConfirmModal(false);
      console.error('Audit save failed:', err);
      showToast('error', 'Save Failed', 'Audit save failed. Delete was not performed.', true);
    }
  };

  const handleClear = () => {
    setFormKey((k) => k + 1);
    setCao('');
    resetFromBranch();
    setTouched({});
    setPendingDeletePayload(null);
  };

  const totalPages = Math.ceil(guarantorRecords.length / ROWS_PER_PAGE);
  const showPaginator = guarantorRecords.length > ROWS_PER_PAGE;
  const pagedRecords = showPaginator
    ? guarantorRecords.slice(page * ROWS_PER_PAGE, page * ROWS_PER_PAGE + ROWS_PER_PAGE)
    : guarantorRecords;

  return (
    <div className="nc-page">
      <Toast toast={toast} onClose={() => setToast(null)} testId="mvo-guarantor-toast" />

      <div style={{ background: 'white', border: '1px solid #E5E7EB', borderRadius: '8px', padding: '20px', position: 'relative' }} data-testid="mvo-guarantor-delete-form">
        {isSaving && (
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '3px', background: '#002FA7', opacity: 0.6, borderRadius: '8px 8px 0 0' }} />
        )}

        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
          <div style={{ width: '34px', height: '34px', borderRadius: '8px', background: '#EFF4FF', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <UserMinus size={18} color="#002FA7" />
          </div>
          <div>
            <div style={{ fontSize: '11px', fontWeight: '600', color: '#002FA7', textTransform: 'uppercase' }}>Guarantor Management</div>
            <div style={{ fontSize: '16px', fontWeight: '700', color: '#111827' }}>MVO Guarantor Delete</div>
          </div>
        </div>

        {/* SECTION A: Select Ticket */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
          <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: '#002FA7', color: 'white', fontSize: '12px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>A</div>
          <div>
            <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>Select Ticket</div>
            <div style={{ fontSize: '11px', color: '#9CA3AF' }}>Choose branch, group code and ticket to load guarantor records</div>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
          <div>
            <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#6B7280', marginBottom: '4px' }}>
              CAO <span style={{ color: '#DC2626' }}>*</span>
            </label>
            <SearchableSelect
              key={`cao-${formKey}`}
              options={branchlist}
              getLabel={(o) => o.branch_name}
              getValue={(o) => o.branch_name}
              value={cao}
              onChange={handleBranchChange}
              placeholder="Select branch"
              emptyMessage="No branches found"
              error={caoInvalid}
              testId="mvo-cao-select"
            />
            {caoInvalid && <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '2px' }}>CAO is required</div>}
          </div>

          <div>
            <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#6B7280', marginBottom: '4px' }}>
              Group Code <span style={{ color: '#DC2626' }}>*</span>
            </label>
            <SearchableSelect
              key={`group-${formKey}`}
              options={branchCodeList}
              getLabel={(o) => o.group_Code}
              getValue={(o) => o.group_Code}
              value={group}
              onChange={handleGroupChange}
              placeholder="Select group code"
              emptyMessage="Select a CAO first"
              disabled={!branchschema}
              error={groupInvalid}
              testId="mvo-group-select"
            />
            {groupInvalid && <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '2px' }}>Group Code is required</div>}
          </div>

          <div>
            <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#6B7280', marginBottom: '4px' }}>
              Ticket No <span style={{ color: '#DC2626' }}>*</span>
            </label>
            <SearchableSelect
              key={`ticket-${formKey}`}
              options={tickets}
              getLabel={(o) => String(o.ticketno)}
              getValue={(o) => o.ticketno}
              value={ticket}
              onChange={handleTicketChange}
              placeholder="Select ticket"
              emptyMessage="Select a group code first"
              disabled={!groupCode}
              error={ticketInvalid}
              testId="mvo-ticket-select"
            />
            {ticketInvalid && <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '2px' }}>Ticket No is required</div>}
          </div>
        </div>

        {(guarantorRecords.length > 0 || ticket) && (
          <div style={{ height: '1px', background: '#E5E7EB', margin: '20px 0' }} />
        )}

        {/* SECTION B: Guarantor Records */}
        {guarantorRecords.length > 0 && (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
              <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: '#002FA7', color: 'white', fontSize: '12px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>B</div>
              <div>
                <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>Guarantor Records</div>
                <div style={{ fontSize: '11px', color: '#9CA3AF' }}>Select one or more records to remove</div>
              </div>
            </div>

            {selectedGuarantors.length > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '10px', marginBottom: '8px' }}>
                <span style={{ fontSize: '11px', fontWeight: '700', color: '#002FA7', background: '#EFF4FF', padding: '4px 10px', borderRadius: '999px' }}>
                  {selectedGuarantors.length} record(s) selected
                </span>
                <button type="button" onClick={clearSelection} className="btn-secondary" style={{ padding: '6px 12px', fontSize: '12px' }} data-testid="mvo-clear-selection">
                  Clear Selection
                </button>
              </div>
            )}

            <div style={{ overflowX: 'auto', border: '1px solid #E5E7EB', borderRadius: '8px' }}>
              <table style={{ width: '100%', minWidth: '640px', borderCollapse: 'collapse', fontSize: '13px' }} data-testid="mvo-guarantor-table">
                <thead>
                  <tr style={{ background: '#F9FAFB', borderBottom: '1px solid #E5E7EB' }}>
                    <th style={{ textAlign: 'left', padding: '10px 12px', fontSize: '11px', color: '#6B7280' }}>Guarantor ID</th>
                    <th style={{ textAlign: 'left', padding: '10px 12px', fontSize: '11px', color: '#6B7280' }}>Contact ID</th>
                    <th style={{ textAlign: 'left', padding: '10px 12px', fontSize: '11px', color: '#6B7280' }}>Guarantor Name</th>
                    <th style={{ textAlign: 'left', padding: '10px 12px', fontSize: '11px', color: '#6B7280' }}>MVO Reference No</th>
                    <th style={{ width: '8rem', textAlign: 'center', padding: '10px 12px', fontSize: '11px', color: '#6B7280' }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {pagedRecords.map((row) => {
                    const rowSelected = isSelected(row);
                    const rowContactId = getContactId(row);
                    const rowRef = getReferenceValue(row);
                    const rowDisabled = !rowContactId || !rowRef;
                    return (
                      <tr
                        key={getGuarantorId(row)}
                        style={{ borderBottom: '1px solid #F3F4F6', background: rowSelected ? '#FEF2F2' : 'transparent' }}
                      >
                        <td style={{ padding: '10px 12px', fontFamily: 'monospace' }}>{getGuarantorId(row)}</td>
                        <td style={{ padding: '10px 12px', fontFamily: 'monospace' }}>{rowContactId || '—'}</td>
                        <td style={{ padding: '10px 12px' }}>{row.guarantorName || row.guarantor_name || row.name || 'Unnamed'}</td>
                        <td style={{ padding: '10px 12px', fontFamily: 'monospace' }}>
                          {rowRef || <span style={{ color: '#9CA3AF', fontStyle: 'italic' }}>Resolving…</span>}
                        </td>
                        <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                          <button
                            type="button"
                            onClick={() => toggleSelect(row)}
                            disabled={rowDisabled}
                            title={rowDisabled ? 'Missing contact id or MVO reference id' : ''}
                            className={rowSelected ? undefined : 'btn-secondary'}
                            style={rowSelected ? {
                              padding: '6px 12px', fontSize: '12px', background: '#DC2626', color: 'white',
                              border: '1px solid #DC2626', borderRadius: '6px', cursor: 'pointer',
                            } : { padding: '6px 12px', fontSize: '12px' }}
                          >
                            {rowSelected ? 'Selected' : 'Select'}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {showPaginator && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '12px', padding: '10px 12px', borderTop: '1px solid #E5E7EB', background: '#F9FAFB' }}>
                  <button
                    type="button"
                    onClick={() => setPage((p) => Math.max(0, p - 1))}
                    disabled={page === 0}
                    className="btn-secondary"
                    style={{ padding: '4px 8px', display: 'flex', alignItems: 'center', opacity: page === 0 ? 0.5 : 1 }}
                    data-testid="mvo-page-prev"
                  >
                    <CaretLeft size={12} />
                  </button>
                  <span style={{ fontSize: '11px', color: '#6B7280' }}>
                    Page {page + 1} of {totalPages}
                  </span>
                  <button
                    type="button"
                    onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                    disabled={page >= totalPages - 1}
                    className="btn-secondary"
                    style={{ padding: '4px 8px', display: 'flex', alignItems: 'center', opacity: page >= totalPages - 1 ? 0.5 : 1 }}
                    data-testid="mvo-page-next"
                  >
                    <CaretRight size={12} />
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Empty state */}
        {ticket && guarantorRecords.length === 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', padding: '30px', color: '#9CA3AF' }}>
            <Tray size={28} />
            <div style={{ fontSize: '13px' }}>No guarantor records found for the selected ticket.</div>
          </div>
        )}

        {/* SECTION C: Reason */}
        {guarantorRecords.length > 0 && (
          <>
            <div style={{ height: '1px', background: '#E5E7EB', margin: '20px 0' }} />
            <SectionLock locked={selectedGuarantors.length === 0} hint="Select at least one guarantor record in section B first">
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
              <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: '#002FA7', color: 'white', fontSize: '12px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>C</div>
              <div>
                <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>Reason</div>
                <div style={{ fontSize: '11px', color: '#9CA3AF' }}>Provide a reason for this deletion (min 10 characters)</div>
              </div>
            </div>

            <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#6B7280', marginBottom: '4px' }}>
              Reason <span style={{ color: '#DC2626' }}>*</span>
            </label>
            <textarea
              className="input-field"
              style={{ fontSize: '13px', borderColor: reasonInvalid ? '#DC2626' : undefined }}
              rows={3}
              maxLength={200}
              placeholder="Enter reason (min 10 characters)..."
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              onBlur={() => markTouched('reason')}
              onKeyDown={(e) => preventExceedLength(e, 200)}
              data-testid="mvo-reason"
            />
            {reasonInvalid && (
              <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '2px' }}>
                {reason.trim().length === 0 ? 'Reason is required.' : reason.trim().length < 10 ? 'Minimum 10 characters required.' : 'Maximum 200 characters allowed.'}
              </div>
            )}
            </SectionLock>
          </>
        )}

        {/* ACTION BAR */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: '#9CA3AF' }}>
            <Info size={13} /> Fields marked with <strong>*</strong> are required
          </div>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button type="button" onClick={handleClear} className="btn-secondary" disabled={isSaving} data-testid="mvo-clear-button">
              <ArrowClockwise size={14} style={{ marginRight: '4px' }} /> Clear
            </button>
            <button
              type="button"
              onClick={confirmDelete}
              disabled={isSaving || selectedGuarantors.length === 0}
              data-testid="mvo-delete-button"
              style={{
                padding: '8px 16px', fontSize: '13px', fontWeight: '600', color: 'white',
                background: (isSaving || selectedGuarantors.length === 0) ? '#F3A6A6' : '#DC2626',
                border: 'none', borderRadius: '6px',
                cursor: (isSaving || selectedGuarantors.length === 0) ? 'not-allowed' : 'pointer',
              }}
            >
              <Trash size={14} style={{ marginRight: '4px' }} />
              {isSaving ? 'Deleting...' : selectedGuarantors.length > 0 ? `Delete (${selectedGuarantors.length})` : 'Delete'}
            </button>
          </div>
        </div>
      </div>

      {/* Confirm modal (replaces window.confirm) */}
      {showConfirmModal && (
        <div
          onClick={cancelConfirm}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(17,24,39,0.5)', display: 'flex',
            alignItems: 'center', justifyContent: 'center', zIndex: 50,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ background: 'white', borderRadius: '10px', width: '420px', maxWidth: '90vw', overflow: 'hidden' }}
            data-testid="mvo-confirm-modal"
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid #E5E7EB' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <Trash size={18} color="#DC2626" />
                <h5 style={{ margin: 0, fontSize: '15px', fontWeight: '700', color: '#111827' }}>Confirm Guarantor Deletion</h5>
              </div>
              <button type="button" onClick={cancelConfirm} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9CA3AF' }}>
                <X size={16} />
              </button>
            </div>

            <div style={{ padding: '20px' }}>
              <p style={{ fontSize: '13px', color: '#6B7280', marginTop: 0 }}>
                You&apos;re about to delete <strong>{pendingCount}</strong> guarantor record(s) for ticket <strong>{ticket}</strong>. This action cannot be undone.
              </p>

              <div style={{ display: 'flex', gap: '8px', fontSize: '12px', color: '#6B7280', background: '#F9FAFB', padding: '10px', borderRadius: '6px' }}>
                <Info size={14} style={{ flexShrink: 0, marginTop: '2px' }} />
                <span>This action will be recorded in the audit log with the reason: &quot;{pendingReason}&quot;</span>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', padding: '14px 20px', borderTop: '1px solid #E5E7EB' }}>
              <button type="button" onClick={cancelConfirm} disabled={isSaving} className="btn-secondary" data-testid="mvo-confirm-cancel">
                Cancel
              </button>
              <button
                type="button"
                onClick={doDelete}
                disabled={isSaving}
                data-testid="mvo-confirm-delete"
                style={{
                  display: 'inline-flex', alignItems: 'center', padding: '8px 16px', fontSize: '13px', fontWeight: '600',
                  color: 'white', background: isSaving ? '#F3A6A6' : '#DC2626', border: 'none', borderRadius: '6px',
                  cursor: isSaving ? 'not-allowed' : 'pointer',
                }}
              >
                <Check size={14} style={{ marginRight: '4px' }} /> Yes, Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default MvoGuarantorDeleteForm;
